import { useCallback, useEffect, useMemo, useState, type FormEvent } from 'react';
import { useBlocker } from 'react-router-dom';
import type { Layout } from '@kioskforge/shared';
import { layoutRunsOn } from '@kioskforge/layout';
import { Badge, Button, Dialog, Input, Select, Table } from '../components/ui';
import { MediaPicker } from '../components/MediaPicker';
import { FieldListEditor } from '../components/FieldListEditor';
import { ThresholdEditor } from '../components/ThresholdEditor';
import { DevicePreviewPanel } from '../components/preview/DevicePreviewPanel';
import { ConfirmDialog, EmptyStateBlock, ErrorState, LoadingState } from '../components/states';
import { useToast } from '../app/ToastProvider';
import { useSession } from '../app/session';
import { ApiError } from '../api/client';
import { deviceProfile } from '../features/layouts/deviceProfile';
import {
  checkLayoutUsage,
  createLayout,
  deleteLayout,
  listLayouts,
  listPresets,
  publishLayout,
  renameLayout,
  saveDraftConfig,
  type LayoutRecord,
  type PresetRecord
} from '../api/layouts';
import { presetConfigFields, type PresetConfigField } from '@kioskforge/presets';

/** Human-readable reasons from an `invalid_config` rejection (field-level). */
function configErrors(error: unknown): string {
  if (error instanceof ApiError && Array.isArray(error.details)) {
    return error.details.map((detail) => `${String((detail as { path?: string }).path ?? '')}: ${String((detail as { message?: string }).message ?? '')}`).join('; ');
  }
  return error instanceof Error ? error.message : 'Could not save the layout.';
}

/** The draft currently being edited (A5b.1); `null` means the form is creating a new layout. */
type EditMode = {
  id: string;
  name: string;
  presetKey: string;
  values: Record<string, string>;
  compiled: Layout | null;
};

export function Layouts() {
  const { session } = useSession();
  const { notify } = useToast();
  const writable = (session?.role ?? 'viewer') !== 'viewer';

  const [presets, setPresets] = useState<PresetRecord[]>([]);
  const [layouts, setLayouts] = useState<LayoutRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [edit, setEdit] = useState<EditMode | null>(null);
  const [presetKey, setPresetKey] = useState('');
  const [name, setName] = useState('');
  const [values, setValues] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState('');
  const [busy, setBusy] = useState(false);
  const [mediaBusy, setMediaBusy] = useState(false);
  const [publishingId, setPublishingId] = useState('');

  const [renameTarget, setRenameTarget] = useState<LayoutRecord | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const [deleteTarget, setDeleteTarget] = useState<LayoutRecord | null>(null);
  const [deleteDetail, setDeleteDetail] = useState('');
  const [previewTarget, setPreviewTarget] = useState<LayoutRecord | null>(null);

  const load = useCallback(async () => {
    try {
      const [presetList, layoutList] = await Promise.all([listPresets(), listLayouts()]);
      setPresets(presetList);
      setLayouts(layoutList);
      setError('');
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Could not load layouts.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const fields: PresetConfigField[] = useMemo(() => (presetKey ? presetConfigFields(presetKey) : []), [presetKey]);
  const selectedPreset = presets.find((preset) => preset.key === presetKey);

  const dirty = Boolean(edit && (name.trim() !== edit.name || JSON.stringify(values) !== JSON.stringify(edit.values)));
  const creatingDirty = edit === null && Boolean(presetKey || name.trim() || Object.keys(values).length > 0);

  // A5b.4 — unsaved-changes guard: confirm before in-app navigation drops edits.
  const blocker = useBlocker(() => (dirty || creatingDirty) && !busy);
  useEffect(() => {
    if (blocker.state !== 'blocked') return;
    if (window.confirm('Discard unsaved layout changes?')) blocker.proceed();
    else blocker.reset();
  }, [blocker]);

  function buildConfig(): Record<string, unknown> {
    const config: Record<string, unknown> = {};
    for (const field of fields) {
      const raw = (values[field.key] ?? '').trim();
      if (raw === '') continue;
      config[field.key] = field.type === 'number' ? Number(raw) : raw;
    }
    return config;
  }

  function valuesFromConfig(config: Record<string, unknown>): Record<string, string> {
    const out: Record<string, string> = {};
    for (const [key, value] of Object.entries(config)) {
      if (value === undefined || value === null) continue;
      out[key] = String(value);
    }
    return out;
  }

  function startCreate() {
    setEdit(null);
    setPresetKey('');
    setName('');
    setValues({});
    setFormError('');
  }

  function startEdit(layout: LayoutRecord) {
    const presetKeyForLayout = layout.expand?.preset?.key ?? '';
    const editValues = valuesFromConfig((layout.config ?? {}) as Record<string, unknown>);
    setEdit({
      id: layout.id,
      name: layout.name ?? '',
      presetKey: presetKeyForLayout,
      values: editValues,
      compiled: layout.compiled ?? null
    });
    setPresetKey(presetKeyForLayout);
    setName(layout.name ?? '');
    setValues(editValues);
    setFormError('');
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!presetKey) {
      setFormError('Choose a preset first.');
      return;
    }
    setBusy(true);
    setFormError('');
    try {
      if (edit) {
        // A5b.1 — save the amended config in place (status/version unchanged).
        await saveDraftConfig(edit.id, buildConfig());
        if (name.trim() && name.trim() !== edit.name) await renameLayout(edit.id, name.trim());
        notify(`"${name.trim() || edit.name}" saved`);
      } else {
        const created = await createLayout({
          preset_key: presetKey,
          name: name.trim() || (selectedPreset?.name ?? 'Layout'),
          config: buildConfig()
        });
        // Create-and-publish in one human action so the layout is immediately deployable.
        await publishLayout(created.layout_id);
        notify(`"${created.name}" published`);
      }
      startCreate();
      await load();
    } catch (cause) {
      setFormError(configErrors(cause));
    } finally {
      setBusy(false);
    }
  }

  async function publish(id: string) {
    setPublishingId(id);
    setError('');
    try {
      const result = await publishLayout(id);
      notify(`Published v${result.version}`);
      await load();
    } catch (cause) {
      setError(configErrors(cause));
    } finally {
      setPublishingId('');
    }
  }

  function requestRename(layout: LayoutRecord) {
    setRenameTarget(layout);
    setRenameValue(layout.name ?? '');
  }

  async function confirmRename() {
    if (!renameTarget) return;
    const next = renameValue.trim();
    if (!next) return;
    try {
      await renameLayout(renameTarget.id, next);
      notify(`Renamed to "${next}"`);
      setRenameTarget(null);
      await load();
    } catch (cause) {
      setError(configErrors(cause));
      setRenameTarget(null);
    }
  }

  async function requestDelete(layout: LayoutRecord) {
    setDeleteTarget(layout);
    setDeleteDetail('Checking where this layout is deployed…');
    try {
      const usage = await checkLayoutUsage(layout.id);
      setDeleteDetail(
        usage.deployments > 0
          ? `This layout is used by ${usage.deployments} deployment${usage.deployments === 1 ? '' : 's'}; remove those first.`
          : 'This layout is not deployed to any device. Deleting it cannot be undone.'
      );
    } catch {
      setDeleteDetail('Could not check deployments. Deleting cannot be undone.');
    }
  }

  async function confirmDelete() {
    if (!deleteTarget) return;
    try {
      await deleteLayout(deleteTarget.id);
      notify(`Deleted "${deleteTarget.name ?? deleteTarget.id}"`);
      setDeleteTarget(null);
      await load();
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : 'Could not delete the layout.');
      setDeleteTarget(null);
    }
  }

  const deviceClasses = (selectedPreset?.device_classes as Array<'tablet' | 'esp32'> | undefined)
    ?? (['tablet', 'esp32'] as const);

  return (
    <main className="workspace-main">
      <header className="page-header">
        <div>
          <span className="index-label">CONTROL ROOM / LAYOUTS</span>
          <h1>Layouts</h1>
        </div>
      </header>

      {loading && <LoadingState label="Loading layouts" />}
      {error && <ErrorState title="Layouts unavailable" detail={error} onRetry={() => void load()} />}

      {!loading && writable && (
        <section className="panel">
          <header className="panel-heading">
            <span className="index-label">{edit ? 'EDIT LAYOUT' : 'NEW LAYOUT'}</span>
            {edit && (
              <Button variant="quiet" type="button" onClick={startCreate}>
                Cancel edit
              </Button>
            )}
          </header>
          <div className="panel-body">
            {formError && (
              <p className="dialog-alert" role="alert">
                <span aria-hidden="true">▲</span>
                {formError}
              </p>
            )}
            <form onSubmit={submit} className="settings-form">
              <label className="index-label" htmlFor="preset-select">
                PRESET
              </label>
              <Select
                id="preset-select"
                value={presetKey}
                disabled={Boolean(edit)}
                onChange={(event) => {
                  setPresetKey(event.target.value);
                  setValues({});
                  setFormError('');
                }}
              >
                <option value="">Choose a preset</option>
                {presets.map((preset) => (
                  <option key={preset.key} value={preset.key}>
                    {preset.name ?? preset.key} — {preset.sector ?? ''}
                  </option>
                ))}
              </Select>
              {selectedPreset && (
                <p className="detail-note">
                  Claimed for {(selectedPreset.device_classes ?? []).join(', ') || 'n/a'} · lockdown{' '}
                  {selectedPreset.min_lockdown ?? 'none'}
                </p>
              )}

              <label className="index-label" htmlFor="layout-name">
                NAME
              </label>
              <Input
                id="layout-name"
                placeholder={selectedPreset?.name ?? 'e.g. Reception signage'}
                value={name}
                onChange={(event) => setName(event.target.value)}
              />

              {fields.map((field) => (
                <div key={field.key}>
                  <label className="index-label" htmlFor={`cfg-${field.key}`}>
                    {field.label.toUpperCase()}
                    {field.required ? '' : ' (OPTIONAL)'}
                  </label>
                  {field.control?.kind === 'media' ? (
                    <MediaPicker
                      id={`cfg-${field.key}`}
                      value={values[field.key] ?? ''}
                      onChange={(next) => setValues((current) => ({ ...current, [field.key]: next }))}
                      onBusyChange={setMediaBusy}
                    />
                  ) : field.control?.kind === 'fields' ? (
                    <FieldListEditor
                      id={`cfg-${field.key}`}
                      value={values[field.key] ?? ''}
                      onChange={(next) => setValues((current) => ({ ...current, [field.key]: next }))}
                    />
                  ) : field.control?.kind === 'threshold' ? (
                    <ThresholdEditor
                      id={`cfg-${field.key}`}
                      value={values[field.key] ?? ''}
                      onChange={(next) => setValues((current) => ({ ...current, [field.key]: next }))}
                    />
                  ) : field.control?.kind === 'duration' ? (
                    <>
                      <Input
                        id={`cfg-${field.key}`}
                        type="number"
                        inputMode="numeric"
                        min={field.min}
                        max={field.max}
                        value={values[field.key] ?? ''}
                        onChange={(event) => setValues((current) => ({ ...current, [field.key]: event.target.value }))}
                      />
                      <p className="detail-note">
                        Seconds (s){field.min !== undefined || field.max !== undefined ? ` · ${field.min ?? ''}${field.max !== undefined ? `–${field.max}` : ''} s` : ''}
                      </p>
                    </>
                  ) : (
                    <Input
                      id={`cfg-${field.key}`}
                      type={field.type === 'number' ? 'number' : 'text'}
                      inputMode={field.type === 'number' ? 'numeric' : undefined}
                      min={field.min}
                      max={field.max}
                      placeholder={
                        field.type === 'number'
                          ? field.min !== undefined || field.max !== undefined
                            ? `${field.min ?? ''}${field.max !== undefined ? `–${field.max}` : ''}`
                            : ''
                          : field.minLength
                            ? `at least ${field.minLength} characters`
                            : ''
                      }
                      value={values[field.key] ?? ''}
                      onChange={(event) => setValues((current) => ({ ...current, [field.key]: event.target.value }))}
                    />
                  )}
                </div>
              ))}

              {edit?.compiled && (
                <CapabilityWarnings
                  compiled={edit.compiled}
                  minLockdown={selectedPreset?.min_lockdown as 'none' | 'pwa' | 'shell' | undefined}
                  classes={[...deviceClasses]}
                />
              )}

              <div className="settings-actions">
                <Button type="submit" disabled={busy || mediaBusy || !presetKey}>
                  {busy ? 'Saving…' : edit ? 'Save changes' : 'Create & publish'}
                </Button>
              </div>
              <p className="detail-note">
                {edit
                  ? 'Saving updates the layout in place. Publish it from the table to make the change deployable.'
                  : 'The layout is validated against the preset and published so it can be deployed to a screen.'}
              </p>
            </form>
          </div>
        </section>
      )}

      {!loading && !error && (
        <section className="panel">
          <header className="panel-heading">
            <span className="index-label">LAYOUTS / {String(layouts.length).padStart(2, '0')}</span>
          </header>
          {layouts.length === 0 ? (
            <div className="panel-body">
              <EmptyStateBlock title="No layouts yet" detail="Create one above from a preset, then deploy it to a screen." />
            </div>
          ) : (
            <Table>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Preset</th>
                  <th>Status</th>
                  <th>Version</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {layouts.map((layout) => (
                  <tr key={layout.id}>
                    <td>{layout.name ?? layout.id}</td>
                    <td>{layout.expand?.preset?.name ?? layout.preset ?? '—'}</td>
                    <td>
                      <Badge>{layout.status ?? 'draft'}</Badge>
                    </td>
                    <td className="mono">v{layout.version ?? 0}</td>
                    <td>
                      {writable && (
                        <div className="row-actions">
                          <Button variant="quiet" type="button" onClick={() => startEdit(layout)}>
                            Edit
                          </Button>
                          <Button variant="quiet" type="button" onClick={() => requestRename(layout)}>
                            Rename
                          </Button>
                          <Button variant="quiet" type="button" onClick={() => setPreviewTarget(layout)}>
                            Preview
                          </Button>
                          {/* FB-0.5: content with no blocks cannot be published. */}
                          <Button
                            variant="quiet"
                            type="button"
                            disabled={
                              publishingId === layout.id ||
                              !(layout.compiled?.screens ?? []).some((screen) => screen.blocks.length > 0)
                            }
                            title={!(layout.compiled?.screens ?? []).some((screen) => screen.blocks.length > 0)
                              ? 'Add a headline, a picture or a block before publishing.'
                              : undefined}
                            onClick={() => void publish(layout.id)}
                          >
                            {layout.status === 'published' ? 'Republish' : 'Publish'}
                          </Button>
                          <Button variant="danger" type="button" onClick={() => void requestDelete(layout)}>
                            Delete
                          </Button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </section>
      )}

      <Dialog open={Boolean(renameTarget)} title="Rename layout" onClose={() => setRenameTarget(null)}>
        <label className="index-label" htmlFor="rename-layout">
          NAME
        </label>
        <Input id="rename-layout" value={renameValue} onChange={(event) => setRenameValue(event.target.value)} />
        <div className="dialog-actions">
          <Button variant="quiet" type="button" onClick={() => setRenameTarget(null)}>
            Cancel
          </Button>
          <Button type="button" disabled={!renameValue.trim()} onClick={() => void confirmRename()}>
            Rename
          </Button>
        </div>
      </Dialog>

      <ConfirmDialog
        open={Boolean(deleteTarget)}
        title="Delete layout"
        confirmWord="delete"
        detail={deleteDetail}
        onConfirm={() => void confirmDelete()}
        onClose={() => setDeleteTarget(null)}
      />

      <Dialog open={Boolean(previewTarget)} title={`Preview — ${previewTarget?.name ?? ''}`} onClose={() => setPreviewTarget(null)}>
        {previewTarget && (previewTarget.compiled
          ? <DevicePreviewPanel layout={previewTarget.compiled} />
          : <p className="detail-note">This layout has not been compiled yet — publish it, then open the preview.</p>)}
      </Dialog>
    </main>
  );
}

/**
 * A5b.3 — capability warnings. Runs `layoutRunsOn` for the saved layout's
 * compiled form against each device class and lists why a class cannot run it.
 */
function CapabilityWarnings({
  compiled,
  minLockdown,
  classes
}: {
  compiled: Layout;
  minLockdown?: 'none' | 'pwa' | 'shell';
  classes: Array<'tablet' | 'esp32'>;
}) {
  return (
    <div className="capability-note">
      <span className="index-label">RUNS ON</span>
      <ul className="capability-list">
        {classes.map((deviceClass) => {
          const result = layoutRunsOn(compiled, deviceProfile(deviceClass), minLockdown);
          return (
            <li key={deviceClass} className={result.ok ? 'capability-ok' : 'capability-blocked'}>
              <strong>{deviceClass}</strong>
              {result.ok ? ' — runs' : ` — blocked: ${result.reasons.join(' ')}`}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
